import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

/**
 * Common handle style for connection points
 */
const handleStyle = {
  width: 15,
  height: 15,
  backgroundColor: '#67b35d',
  border: '2px solid #fff'
};

/**
 * Node component with handles configuration
 */
export function BaseNode({
  id,
  nodeData,
  icon,
  color,
  handleConf = [],
  children = null,
  onEditNode,
  onDeleteNode
}) {
  const { formatMessage: msg } = useIntl();
  const theme = useTheme();

  const handles = handleConf && handleConf.length > 0 ? handleConf : nodeData?.handleConf;

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  const handleOpenDeleteDialog = (event) => {
    event.stopPropagation();
    setOpenConfirmDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmDelete = () => {
    onDeleteNode && onDeleteNode(id);
    handleCloseDeleteDialog();
  };

  const handleContainerEdit = (event) => {
    if (onEditNode) {
      if (!isDeleteHovered) {
        event.stopPropagation();
        onEditNode(id);
      }
    }
  };

  const editButtonBackgroundColor = isDeleteHovered
    ? 'transparent'
    : isHovered
      ? theme.palette.grey[200]
      : 'transparent';

  return (
    <MDBox
      elevation="1"
      sx={{
        background: 'white',
        position: 'relative',
        padding: '12px',
        border: (theme) => `2px solid ${theme.palette[color].main}dd`,
        borderRadius: '15px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
        minWidth: '350px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover .node-actions': {
          opacity: 1,
          pointerEvents: 'auto'
        }
      }}
    >
      {/* Boutons d’action */}
      <MDBox
        id="actionContainer"
        onClick={handleContainerEdit} // On utilise le conteneur pour déclencher le clic (si on n'est pas sur le bouton delete)
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: '100%',
          height: onEditNode ? '50px' : '0px',
          position: 'absolute',
          background: 'transparent',
          top: 0,
          cursor: onEditNode ? 'pointer' : 'default'
        }}
      >
        <MDBox
          className="node-actions"
          sx={{
            position: 'absolute',
            top: 1,
            right: 4,
            display: 'flex',
            gap: 0.5,
            // opacity: 0,
            transition: 'opacity 0.1s ease-in-out',
            pointerEvents: 'none',
            background: 'white',
            borderRadius: '8px',
            padding: '2px'
          }}
        >
          {onEditNode && (
            <Tooltip title={msg({ id: 'button.modify' })}>
              <IconButton
                size="medium"
                color="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditNode(id);
                }}
                sx={{
                  backgroundColor: editButtonBackgroundColor,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[200]
                  }
                }}
              >
                <Icon fontSize="medium">edit</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={msg({ id: 'button.delete' })}>
            <IconButton
              size="medium"
              color="error"
              onClick={handleOpenDeleteDialog}
              onMouseEnter={() => setIsDeleteHovered(true)}
              onMouseLeave={() => setIsDeleteHovered(false)}
              sx={{
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.2)
                }
              }}
            >
              <Icon fontSize="medium">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>

      {/* Icône et label */}
      <MDBox sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Icon fontSize="medium">{icon}</Icon>
        <MDBox ml={2} fontWeight="bold">
          {nodeData.label}
        </MDBox>
      </MDBox>

      {/* Contenu du nœud */}
      {children}

      {/* Handles de connexion */}
      {Array.isArray(handles) &&
        handles.map((conf, index) => (
          <Handle
            key={index}
            type={conf.type}
            position={conf.position}
            id={conf.id ?? conf.position}
            style={{ ...handleStyle, ...conf.style }}
          />
        ))}

      {/* Composant de Dialogue de Confirmation */}
      <Dialog open={openConfirmDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{msg({ id: 'dialog.delete.confirm.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg({ id: 'dialog.delete.confirm.message' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDeleteDialog} color="secondary">
            {msg({ id: 'button.cancel' })}
          </MDButton>
          <MDButton onClick={handleConfirmDelete} color="error">
            {msg({ id: 'button.delete' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

BaseNode.propTypes = {
  // ... (PropTypes inchangés)
};

export default BaseNode;

BaseNode.propTypes = {
  id: PropTypes.string,
  nodeData: PropTypes.shape({
    label: PropTypes.string.isRequired,
    id: PropTypes.string,
    code: PropTypes.string,
    content: PropTypes.string,
    type: PropTypes.string,
    handleConf: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['source', 'target']).isRequired,
        position: PropTypes.oneOf([Position.Top, Position.Right, Position.Bottom, Position.Left])
      })
    )
  }).isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  handleConf: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['source', 'target']).isRequired,
      position: PropTypes.oneOf([Position.Top, Position.Right, Position.Bottom, Position.Left])
        .isRequired
    })
  ),
  children: PropTypes.node,
  onEditNode: PropTypes.func,
  onDeleteNode: PropTypes.func
};
